import { Evidence, TemporalEvidence } from '../types/types';

// Temporal color mappings
export const TEMPORAL_COLORS = {
  periods: {
    'May 2024': '#2563eb',      // Blue-600
    'December 2024': '#7c3aed', // Violet-600
    'May 2025': '#059669',      // Emerald-600
    'December 2025': '#dc2626', // Red-600
  },
  shiftTypes: {
    'resource_flows': '#dc2626',        // Red-600
    'policy_changes': '#ea580c',        // Orange-600
    'community_engagement': '#16a34a',  // Green-600
    'infrastructure': '#0ea5e9',        // Sky-600
    'relationships': '#a855f7',         // Purple-600
    'mental_models': '#ec4899',         // Pink-600
    'power_dynamics': '#f59e0b',        // Amber-600
    'default': '#6b7280',               // Gray-500
  },
  systems: {
    'food systems': '#16a34a',                    // Green-600
    'commercial and economic systems': '#2563eb', // Blue-600  
    'neighbourhood and built environment': '#9333ea', // Purple-600
    'healthcare systems': '#dc2626',              // Red-600
    'education systems': '#eab308',               // Yellow-600
    'Multiple Systems': '#6b7280',                // Gray-500
    'Unknown': '#6b7280',                         // Gray-500
    'default': '#6b7280',                         // Gray-500
  },
  confidence: {
    high: '#15803d',      // Green-700
    medium: '#eab308',    // Yellow-500
    low: '#dc2626',       // Red-600
    unrated: '#6b7280',   // Gray-500
  },
  implementationStages: {
    'planning': '#f59e0b',         // Amber-600
    'planned': '#fbbf24',          // Amber-400
    'piloting': '#a78bfa',         // Violet-400
    'implementing': '#3b82f6',     // Blue-600
    'implemented': '#10b981',      // Emerald-600
    'embedded': '#059669',         // Emerald-700
    'developing': '#ec4899',       // Pink-600
    'complete': '#15803d',         // Green-700
    'Unknown': '#6b7280',          // Gray-500
    'default': '#6b7280',          // Gray-500
  },
};

// Parse period label to date
export const parsePeriodToDate = (periodLabel: string | null | undefined): Date => {
  if (!periodLabel) return new Date('2024-05-01');
  
  const periodMap: { [key: string]: Date } = {
    'May 2024': new Date('2024-05-01'),
    'December 2024': new Date('2024-12-01'),
    'May 2025': new Date('2025-05-01'),
    'December 2025': new Date('2025-12-01'),
  };

  return periodMap[periodLabel] || new Date('2024-05-01');
};

// Calculate timeline position
export const calculateTimelinePosition = (
  date: Date,
  rangeStart: Date,
  rangeEnd: Date
): number => {
  const startTime = rangeStart.getTime();
  const endTime = rangeEnd.getTime();
  const dateTime = date.getTime();
  
  const position = ((dateTime - startTime) / (endTime - startTime)) * 100;
  return Math.max(0, Math.min(100, position));
};

// Get color for shift type
export const getShiftTypeColor = (shiftType: string | undefined): string => {
  if (!shiftType) return TEMPORAL_COLORS.shiftTypes.default;
  
  const normalized = shiftType.toLowerCase().replace(/\s+/g, '_');
  return TEMPORAL_COLORS.shiftTypes[normalized as keyof typeof TEMPORAL_COLORS.shiftTypes] || 
         TEMPORAL_COLORS.shiftTypes.default;
};

// Get color for system
export const getSystemColor = (system: string | undefined): string => {
  if (!system) return TEMPORAL_COLORS.systems.default;
  
  // Handle "Multiple Systems" category
  if (system === 'Multiple Systems') {
    return TEMPORAL_COLORS.systems['Multiple Systems'];
  }
  
  const normalized = system.toLowerCase();
  
  // Check if it's a multi-system string (contains multiple systems)
  if (normalized.includes(',') || (normalized.includes(' and ') && normalized.length > 50)) {
    // For evidence with multiple systems, return Multiple Systems color
    return TEMPORAL_COLORS.systems['Multiple Systems'];
  }
  
  return TEMPORAL_COLORS.systems[normalized as keyof typeof TEMPORAL_COLORS.systems] || 
         TEMPORAL_COLORS.systems.default;
};

// Get color for confidence level
export const getConfidenceColor = (confidence: number | { score: number } | null | undefined): string => {
  const score = typeof confidence === 'object' ? confidence?.score : confidence;
  
  if (score === null || score === undefined) {
    return TEMPORAL_COLORS.confidence.unrated;
  }
  
  if (score >= 4) return TEMPORAL_COLORS.confidence.high;
  if (score >= 2) return TEMPORAL_COLORS.confidence.medium;
  return TEMPORAL_COLORS.confidence.low;
};

// Get color for implementation stage
export const getImplementationStageColor = (stage: string | undefined): string => {
  if (!stage) return TEMPORAL_COLORS.implementationStages.default;
  
  const normalized = stage.toLowerCase();
  return TEMPORAL_COLORS.implementationStages[normalized as keyof typeof TEMPORAL_COLORS.implementationStages] || 
         TEMPORAL_COLORS.implementationStages.default;
};

// Get confidence label
export const getConfidenceLabel = (confidence: number | { score: number } | null | undefined): string => {
  const score = typeof confidence === 'object' ? confidence?.score : confidence;
  
  if (score === null || score === undefined) return 'Unrated';
  if (score >= 4) return 'High';
  if (score >= 2) return 'Medium';
  return 'Low';
};

// Format date for display
export const formatTimelineDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
};

// Group evidence by time periods
export const groupEvidenceByPeriod = (
  evidence: Evidence[]
): Map<string, Evidence[]> => {
  const groups = new Map<string, Evidence[]>();
  
  evidence.forEach((item) => {
    const period = typeof item.reporting_period === 'string'
      ? item.reporting_period
      : item.reporting_period?.label || 'Unknown';
    if (!groups.has(period)) {
      groups.set(period, []);
    }
    groups.get(period)!.push(item);
  });
  
  return groups;
};

// Calculate period statistics
export const calculatePeriodStats = (evidence: Evidence[]) => {
  const stats = {
    total: evidence.length,
    avgConfidence: 0,
    byShiftType: new Map<string, number>(),
    byConfidenceLevel: new Map<string, number>(),
    bySystems: new Map<string, number>(),
  };
  
  let totalConfidence = 0;
  let confidenceCount = 0;
  
  evidence.forEach((item) => {
    // Shift type counting
    const shiftType = item.shift_type || 'Unknown';
    stats.byShiftType.set(shiftType, (stats.byShiftType.get(shiftType) || 0) + 1);
    
    // Confidence counting
    const confScore = typeof item.confidence === 'object' ? item.confidence?.score : item.confidence;
    if (confScore !== null && confScore !== undefined) {
      totalConfidence += confScore;
      confidenceCount++;
      
      const level = getConfidenceLabel(item.confidence);
      stats.byConfidenceLevel.set(level, (stats.byConfidenceLevel.get(level) || 0) + 1);
    }
    
    // Systems counting
    const system = item.context?.systems_affected || 'Unknown';
    stats.bySystems.set(system, (stats.bySystems.get(system) || 0) + 1);
  });
  
  stats.avgConfidence = confidenceCount > 0 ? totalConfidence / confidenceCount : 0;
  
  return stats;
};

// Generate chart data for Recharts
export const generateChartData = (
  evidence: Evidence[],
  groupBy: 'shift_type' | 'confidence' | 'system' | 'implementation_stage'
) => {
  const periodGroups = groupEvidenceByPeriod(evidence);
  const chartData: Record<string, number | string>[] = [];
  
  // Get all unique categories
  const allCategories = new Set<string>();
  periodGroups.forEach((items) => {
    items.forEach((item) => {
      if (groupBy === 'shift_type') {
        allCategories.add(item.shift_type || 'Unknown');
      } else if (groupBy === 'confidence') {
        allCategories.add(getConfidenceLabel(item.confidence));
      } else if (groupBy === 'system') {
        allCategories.add(item.context?.systems_affected || 'Unknown');
      } else if (groupBy === 'implementation_stage') {
        allCategories.add(item.implementation_stage?.status || 'Unknown');
      }
    });
  });
  
  // Define period order (chronological)
  const periodOrder = ['May 2024', 'December 2024', 'May 2025', 'December 2025'];
  
  // Build chart data
  periodGroups.forEach((items, period) => {
    const dataPoint: Record<string, number | string> = { period };
    
    // Initialize all categories with 0
    allCategories.forEach((category) => {
      dataPoint[category] = 0;
    });
    
    // Count items by category
    items.forEach((item) => {
      let category: string;
      if (groupBy === 'shift_type') {
        category = item.shift_type || 'Unknown';
      } else if (groupBy === 'confidence') {
        category = getConfidenceLabel(item.confidence);
      } else if (groupBy === 'system') {
        // For systems, handle multiple systems as 'Multiple Systems'
        const system = item.context?.systems_affected || 'Unknown';
        if (system.includes(',') || (system.includes(' and ') && system.length > 50)) {
          category = 'Multiple Systems';
        } else {
          category = system;
        }
      } else if (groupBy === 'implementation_stage') {
        category = item.implementation_stage?.status || 'Unknown';
      } else {
        category = 'Unknown';
      }
      dataPoint[category] = (dataPoint[category] || 0) + 1;
    });
    
    chartData.push(dataPoint);
  });
  
  // Sort chart data by period order
  chartData.sort((a, b) => {
    const indexA = periodOrder.indexOf(a.period);
    const indexB = periodOrder.indexOf(b.period);
    return indexA - indexB;
  });
  
  return { chartData, categories: Array.from(allCategories) };
};

// Filter evidence by date range
export const filterByDateRange = (
  evidence: TemporalEvidence[],
  startDate: Date,
  endDate: Date
): TemporalEvidence[] => {
  return evidence.filter((item) => {
    const itemTime = item.parsedDate.getTime();
    return itemTime >= startDate.getTime() && itemTime <= endDate.getTime();
  });
};

// Calculate zoom range based on zoom level
export const calculateZoomRange = (
  centerDate: Date,
  zoomLevel: 'year' | 'quarter' | 'month'
): [Date, Date] => {
  const center = new Date(centerDate);
  let start: Date;
  let end: Date;
  
  switch (zoomLevel) {
    case 'year':
      start = new Date(center.getFullYear(), 0, 1);
      end = new Date(center.getFullYear(), 11, 31);
      break;
    case 'quarter': {
      const quarter = Math.floor(center.getMonth() / 3);
      start = new Date(center.getFullYear(), quarter * 3, 1);
      end = new Date(center.getFullYear(), (quarter + 1) * 3, 0);
      break;
    }
    case 'month':
      start = new Date(center.getFullYear(), center.getMonth(), 1);
      end = new Date(center.getFullYear(), center.getMonth() + 1, 0);
      break;
  }
  
  return [start, end];
};